import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = 'https://carriera-backend.onrender.com/api';

// Ask Kate (candidate inventory Phase 2b, 2 Nov 2026). Ted or Steph leave Kate a
// request on a candidate, job or To Call row (POST /api/kate-requests). The PC3
// task "Carriera - Kate Requests" picks waiting requests up every 30 minutes on
// weekdays 9am-6pm and Kate's reply appears here. Kate's own login (role "agent")
// sees the requests but cannot ask; the backend refuses her anyway.

export const STATUS = {
  waiting:   ['Waiting for Kate', 'bg-gray-50 text-carriera-muted border-carriera-border'],
  working:   ['Kate is on it', 'bg-blue-50 text-blue-700 border-blue-200'],
  done:      ['Done', 'bg-green-50 text-green-700 border-green-200'],
  cannot_do: ['Needs Ted or Steph', 'bg-amber-50 text-amber-700 border-amber-200'],
};

const REPLIED = ['done', 'cannot_do'];

function when(iso) {
  return new Date(iso).toLocaleString('en-MY', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

export function currentUser() {
  return JSON.parse(localStorage.getItem('user') || '{}');
}

export function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

// Marks my unread replies among `rows` as read, then tells the sidebar badge to recount.
export function markSeen(rows) {
  const me = currentUser().id;
  const unseen = rows.filter(r => r.asked_by === me && REPLIED.includes(r.status) && !r.seen_at);
  if (!unseen.length) return;
  Promise.all(unseen.map(r => axios.patch(`${API}/kate-requests/${r.id}/seen`, {}, { headers: authHeaders() })))
    .catch(() => {})
    .finally(() => window.dispatchEvent(new Event('kate-replies-seen')));
}

export function RequestItem({ r, showAbout = false }) {
  const [label, color] = STATUS[r.status] || [r.status, ''];
  const fresh = r.asked_by === currentUser().id && REPLIED.includes(r.status) && !r.seen_at;
  return (
    <div className={`text-xs border px-3 py-2 ${fresh ? 'border-carriera-accent' : 'border-carriera-border'}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-carriera-muted">
          {showAbout && r.about_label && <span className="font-medium text-carriera-text">{r.about_label} · </span>}
          {r.asked_by_name || 'Someone'}, {when(r.created_at)}
          {fresh && <span className="ml-2 text-carriera-accent font-semibold">New reply</span>}
        </p>
        <span className={`px-2 py-0.5 border rounded flex-shrink-0 ${color}`}>{label}</span>
      </div>
      <p className="text-carriera-sec mt-1 whitespace-pre-wrap">{r.request}</p>
      {r.reply && (
        <div className="mt-2 bg-carriera-bg px-3 py-2">
          <p className="font-semibold text-carriera-text mb-0.5">Kate, {when(r.replied_at)}</p>
          <p className="text-carriera-sec whitespace-pre-wrap leading-relaxed">{r.reply}</p>
        </div>
      )}
    </div>
  );
}

// The box itself. startOpen=false keeps it folded (To Call rows) until clicked,
// so a long list does not load every row's requests. showHistory=false shows the
// ask box only (the Ask Kate page lists every request itself).
export default function AskKate({ aboutType, aboutId, aboutLabel, startOpen = true, showHistory = true, placeholder, onAsked }) {
  const [open, setOpen] = useState(startOpen);
  const [rows, setRows] = useState(null);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const canAsk = currentUser().role !== 'agent';

  const load = useCallback(() => {
    const params = aboutType === 'general' ? { about_type: 'general' } : { about_type: aboutType, about_id: aboutId };
    axios.get(`${API}/kate-requests`, { headers: authHeaders(), params })
      .then(({ data }) => { setRows(data); markSeen(data); })
      .catch(() => setError('Failed to load requests to Kate.'));
  }, [aboutType, aboutId]);

  useEffect(() => { if (open && showHistory) load(); }, [open, showHistory, load]);

  async function ask() {
    if (!text.trim()) return;
    setSaving(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/kate-requests`,
        { about_type: aboutType, about_id: aboutId, about_label: aboutLabel, request: text.trim() },
        { headers: authHeaders() });
      setRows(r => [data, ...(r || [])]);
      setText('');
      onAsked?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send the request to Kate.');
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
        className="text-xs border border-carriera-border px-3 py-1.5 text-carriera-sec hover:border-carriera-accent transition-colors">
        Ask Kate
      </button>
    );
  }

  return (
    <div className="space-y-2">
      {showHistory && rows === null && !error && <p className="text-xs text-carriera-muted">Loading...</p>}
      {showHistory && rows?.map(r => <RequestItem key={r.id} r={r} />)}
      {canAsk ? (
        <div>
          <textarea rows={2} value={text} onChange={e => setText(e.target.value)} maxLength={2000}
            placeholder={placeholder || 'e.g. Find 5 more like this one, or draft a WhatsApp asking for the latest payslip'}
            className="w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent resize-y" />
          <div className="flex items-center gap-3 mt-1">
            <button type="button" onClick={ask} disabled={saving || !text.trim()}
              className="text-xs bg-carriera-text text-white px-4 py-1.5 hover:bg-carriera-accent transition-colors disabled:opacity-40">
              {saving ? 'Sending...' : 'Ask Kate'}
            </button>
            <p className="text-xs text-carriera-muted">Kate checks every 30 minutes, weekdays 9am-6pm.</p>
          </div>
        </div>
      ) : (
        <p className="text-xs text-carriera-muted">Only Ted or Steph ask Kate.</p>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
